import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { CommunityTagType, type TAvailableTag } from '@onesource/schemas';
import { useMemo, type FC } from 'react';
import { MdSearch } from 'react-icons/md';

import { FLUID_TYPE } from './fluid-type';

// Tags are grouped by their category (e.g. General / Modules / Condition). Tags
// with no category — content sub-spaces and lessons — fall back to a group derived
// from their type (sub-spaces are the space's modules; lessons stay their own group).
const TYPE_FALLBACK_GROUP: Record<CommunityTagType, string> = {
  [CommunityTagType.QUESTION]: 'Topics',
  [CommunityTagType.SUBSPACE]: 'Modules',
  [CommunityTagType.LESSON]: 'Lessons',
};

export const groupForTagOption = (option: TAvailableTag): string =>
  option.category?.trim() || TYPE_FALLBACK_GROUP[option.type];

// Preferred group order; anything else sorts after these, alphabetically.
const GROUP_PRIORITY = ['General', 'Topics', 'Modules', 'Lessons'];
const groupRank = (group: string): number => {
  const index = GROUP_PRIORITY.indexOf(group);
  return index === -1 ? GROUP_PRIORITY.length : index;
};

/**
 * MUI's groupBy only merges consecutive options, so pre-sort by group
 * (preferred order first, then alphabetical). The sort is stable, so the
 * backend's intra-group ordering (position, label) is preserved.
 */
export const sortTagOptions = (options: TAvailableTag[]): TAvailableTag[] =>
  [...options].sort((a, b) => {
    const groupA = groupForTagOption(a);
    const groupB = groupForTagOption(b);
    if (groupA === groupB) {
      return 0;
    }
    const rankA = groupRank(groupA);
    const rankB = groupRank(groupB);
    return rankA !== rankB ? rankA - rankB : groupA.localeCompare(groupB);
  });

/** Popover paper styling shared by the tag pickers that host this panel. */
export const TAG_PICKER_PAPER_SX = {
  borderRadius: '12px',
  mt: 0.5,
  width: 320,
  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
} as const;

interface TagPickerPanelProps {
  /** Header label, e.g. "Tag your post" (composer) or "Filter by tags" (feed). */
  title: string;
  options: TAvailableTag[];
  value: TAvailableTag[];
  onChange: (tags: TAvailableTag[]) => void;
  loading?: boolean;
  /** Popovers want the search focused on open; the mobile filter drawer
      doesn't — focusing would pop the keyboard the moment a section expands. */
  autoFocusSearch?: boolean;
  /** The drawer's section card already labels the panel — hide the title and
      keep only the clear action (when there is a selection). */
  hideTitle?: boolean;
}

/**
 * The searchable, grouped, checkbox tag list shared by the post composer's
 * tag picker and the feed's tag filter — one styled panel so the two stay
 * identical: header with a clear action, soft filled search input, sticky
 * uppercase group labels, and rounded rows with a primary tint when selected.
 */
export const TagPickerPanel: FC<TagPickerPanelProps> = ({
  title,
  options,
  value,
  onChange,
  loading = false,
  autoFocusSearch = true,
  hideTitle = false,
}) => {
  const sortedOptions = useMemo(() => sortTagOptions(options), [options]);

  return (
    <Box sx={{ p: 1.5 }}>
      {(!hideTitle || value.length > 0) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: hideTitle ? 'flex-end' : 'space-between',
            mb: 1,
            px: 0.5,
          }}
        >
          {!hideTitle && (
            <Typography
              sx={{ fontFamily: 'Figtree, sans-serif', fontSize: FLUID_TYPE.label, fontWeight: 700, color: '#333' }}
            >
              {title}
            </Typography>
          )}
          {value.length > 0 && (
            <Button
              size="small"
              variant="contained"
              onClick={() => onChange([])}
              sx={{
                borderRadius: '10px',
                height: 34,
                minHeight: 34,
                textTransform: 'none',
                fontFamily: 'Figtree, sans-serif',
                fontWeight: 600,
                px: 3,
                boxShadow: 'none',
                '&.MuiButton-containedPrimary:not(.Mui-disabled)': {
                  background: theme => theme.palette.primary.main,
                  color: theme => theme.palette.primary.contrastText,
                  '&:hover': { background: theme => theme.palette.primary.dark, boxShadow: 'none' },
                },
                '&&.Mui-disabled': { background: '#E2E2E2 !important', color: '#9E9E9E' },
              }}
            >
              Clear
            </Button>
          )}
        </Box>
      )}
      <Autocomplete
        multiple
        disableCloseOnSelect
        size="small"
        options={sortedOptions}
        value={value}
        loading={loading}
        onChange={(_event, newValue) => onChange(newValue)}
        groupBy={groupForTagOption}
        getOptionLabel={option => option.label}
        isOptionEqualToValue={(option, selected) => option.type === selected.type && option.value === selected.value}
        noOptionsText={loading ? 'Loading tags…' : 'No tags found'}
        forcePopupIcon={false}
        ListboxProps={{ sx: { maxHeight: 300, py: 0.5 } }}
        // Lift the results off the host surface — without a border + shadow
        // the white option list blends into the panel/drawer behind it.
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              borderRadius: '12px',
              border: '1px solid #f0f0f0',
              boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
            },
          },
        }}
        renderGroup={params => (
          <li key={params.key}>
            <Typography
              sx={{
                position: 'sticky',
                top: -4,
                zIndex: 1,
                bgcolor: 'common.white',
                px: 1.5,
                pt: 1,
                pb: 0.5,
                fontFamily: 'Figtree, sans-serif',
                fontSize: FLUID_TYPE.overline,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: '#9a9a9a',
              }}
            >
              {params.group}
            </Typography>
            <Box component="ul" sx={{ p: 0, m: 0, listStyle: 'none' }}>
              {params.children}
            </Box>
          </li>
        )}
        renderOption={(props, option, { selected }) => {
          const { key, ...optionProps } = props;
          return (
            <Box
              component="li"
              key={key}
              {...optionProps}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                borderRadius: '8px',
                mx: 0.5,
                my: '1px',
                // Match the class MUI puts on options so these paddings and
                // the selected tint win over the default option styles.
                '&.MuiAutocomplete-option': { px: 1, py: '6px', minHeight: 'unset' },
                '&.MuiAutocomplete-option[aria-selected="true"]': {
                  bgcolor: theme => `${theme.palette.primary.main}14`,
                },
                '&.MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
                  bgcolor: theme => `${theme.palette.primary.main}1F`,
                },
              }}
            >
              <Checkbox
                size="small"
                checked={selected}
                sx={{ p: 0, color: 'grey.400', '&.Mui-checked': { color: 'primary.main' } }}
              />
              <Typography
                noWrap
                sx={{
                  fontFamily: 'Figtree, sans-serif',
                  fontSize: FLUID_TYPE.label,
                  fontWeight: selected ? 600 : 400,
                  color: '#333',
                }}
              >
                {option.label}
              </Typography>
            </Box>
          );
        }}
        // Selected tags are shown by the host (chips below the composer, or on
        // the filter trigger button); keep the input clean for searching.
        renderTags={() => null}
        renderInput={params => (
          <TextField
            {...params}
            autoFocus={autoFocusSearch}
            placeholder="Search tags…"
            InputProps={{
              ...params.InputProps,
              // Replace the chip start-adornment (renderTags is null anyway)
              // with a search icon, and drop the popup/clear indicators —
              // the header's "Clear" covers that.
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 0 }}>
                  <MdSearch size={16} color="#9a9a9a" />
                </InputAdornment>
              ),
              endAdornment: loading ? <CircularProgress color="inherit" size={16} /> : null,
              sx: {
                fontFamily: 'Figtree, sans-serif',
                fontSize: FLUID_TYPE.label,
                bgcolor: '#f6f6f6',
                transition: 'background-color 0.15s ease',
                // Theme forces OutlinedInput to 20px; match the selector to override.
                '&.MuiOutlinedInput-root': { borderRadius: '10px' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'grey.300' },
                '&.Mui-focused': { bgcolor: 'common.white' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                  borderWidth: 1,
                },
              },
            }}
          />
        )}
      />
    </Box>
  );
};
